/**
 * PlansTab.js — Member-facing plan picker with upgrade / downgrade / renew.
 *
 * Props:
 *   plans        — array of plan objects from /plans/getPlansList
 *   userDetails  — current user from Redux
 *   planInfo     — current subscribed plan object (may be null for free users)
 *   onSelectPlan — fn(plan) — opens the PaymentModal
 *   t            — translation fn from useLanguage
 */

import React, { useEffect, useRef } from "react"
import { motion } from "framer-motion"
import { useLanguage } from "../../Common/LanguageContext"
import {
    HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineGift,
    HiOutlineArrowUpCircle, HiOutlineArrowDownCircle, HiOutlineStar,
    HiOutlineLockClosed, HiOutlineShieldCheck,
} from "react-icons/hi2"

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Extract a string array of features from a plan object (two formats supported) */
const getPlanFeatures = (plan, lang = "en") => {
    if (!plan) return []
    // Format 1: planBenefits: [{benefits, tamilBenefits}]
    if (Array.isArray(plan.planBenefits) && plan.planBenefits.length) {
        return plan.planBenefits.map(b =>
            (lang === "ta" && b.tamilBenefits) ? b.tamilBenefits : (b.benefits || "")
        ).filter(Boolean)
    }
    // Format 2: features: ["string1", "string2"]
    if (Array.isArray(plan.features) && plan.features.length) return plan.features
    return []
}

// Plan tier colours
const TIER_COLOR = {
    free:     { bg: "bg-slate-50",     border: "border-slate-200",     btn: "bg-slate-600 hover:bg-slate-700"   },
    silver:   { bg: "bg-slate-50",     border: "border-slate-300",     btn: "bg-slate-500 hover:bg-slate-600"   },
    gold:     { bg: "bg-amber-50",     border: "border-amber-300",     btn: "bg-amber-500 hover:bg-amber-600"   },
    diamond:  { bg: "bg-sky-50",       border: "border-sky-300",       btn: "bg-sky-500 hover:bg-sky-600"       },
    platinum: { bg: "bg-violet-50",    border: "border-violet-300",    btn: "bg-violet-600 hover:bg-violet-700" },
    default:  { bg: "bg-primeSoft/30", border: "border-primeColor/30", btn: "bg-primeColor hover:bg-primeDark"  },
}

const getTier = (name = "") => {
    const n = name.toLowerCase()
    if (n.includes("free"))     return "free"
    if (n.includes("silver"))   return "silver"
    if (n.includes("gold"))     return "gold"
    if (n.includes("diamond"))  return "diamond"
    if (n.includes("platinum")) return "platinum"
    return "default"
}

const isPaidActive = (user) => {
    if (!user?.isPaid) return false
    if (!user?.planValidityDate) return true
    return new Date(user.planValidityDate) > new Date()
}

const daysLeft = (date) => {
    if (!date) return null
    const diff = new Date(date) - new Date()
    return diff > 0 ? Math.ceil(diff / 86400000) : 0
}

// ── Component ────────────────────────────────────────────────────────────────
const PlansTab = ({ plans = [], userDetails, planInfo, onSelectPlan, t: tProp }) => {
    const { lang } = useLanguage()
    const t = tProp || ((k) => k)

    const currentPlanId  = userDetails?.planId || null
    const isActive       = isPaidActive(userDetails)
    const expiry         = userDetails?.planValidityDate
    const left           = isActive ? daysLeft(expiry) : null
    const sortedPlans    = [...plans].sort((a, b) => (a.planRate || 0) - (b.planRate || 0))
    const currentCardRef = useRef(null)

    const freePlan = sortedPlans.find(p => p.isFree || p.planRate === 0)
    const paidPlans = sortedPlans.filter(p => !p.isFree && p.planRate > 0)

    // Scroll current plan into view on mount
    useEffect(() => {
        if (currentCardRef.current) {
            currentCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
        }
    }, [])

    const planRank = (id) => sortedPlans.findIndex(p => p._id === id)
    const currentRank = planRank(currentPlanId)

    return (
        <div className="space-y-6">

            {/* ── Current plan banner ── */}
            <div className={`rounded-2xl border p-5 ${
                isActive
                    ? "border-[#cfe8d4] bg-[#f2fbf4]"
                    : "border-amber-200 bg-amber-50"
            }`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <p className={`text-xs font-bold uppercase tracking-[0.14em] ${isActive ? "text-[#1a8a3a]" : "text-amber-700"}`}>
                            {isActive ? (t("activePlan") || "Active Plan") : (t("currentPlan") || "Current Plan")}
                        </p>
                        <h3 className="mt-1 text-xl font-bold text-tColor">
                            {isActive ? (planInfo?.planName || t("paidMember") || "Paid Plan") : (t("freePlan") || "Free Plan")}
                        </h3>
                        {isActive && planInfo?.planRate > 0 && (
                            <p className="text-xs text-slate-500 mt-0.5">₹{planInfo.planRate.toLocaleString("en-IN")}</p>
                        )}
                    </div>
                    {isActive
                        ? <HiOutlineCheckCircle className="h-10 w-10 text-[#1a8a3a] shrink-0" />
                        : <HiOutlineGift        className="h-10 w-10 text-amber-500 shrink-0" />
                    }
                </div>

                {isActive && expiry && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-slate-600">
                            {t("expiresOn") || "Expires"}: {new Date(expiry).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}
                        </span>
                        {left != null && (
                            <span className={`font-bold px-2.5 py-0.5 rounded-full text-xs ${left <= 15 ? "bg-red-100 text-red-700" : left <= 30 ? "bg-amber-100 text-amber-700" : "bg-[#d4f0dc] text-[#1a8a3a]"}`}>
                                {left} {t("daysLeft") || "days left"}
                            </span>
                        )}
                    </div>
                )}

                {!isActive && (
                    <p className="mt-2 text-sm text-amber-800">
                        {t("freePlanDesc") || "You are on the Free Plan — upgrade to unlock full profiles, contact details and horoscope matching."}
                    </p>
                )}
            </div>

            {/* ── Feature comparison (Free vs any paid plan) ── */}
            {(freePlan || paidPlans.length > 0) && (
                <div className="rounded-2xl border border-borderColor bg-white p-5 space-y-4">
                    <h4 className="text-sm font-bold text-tColor">{t("whatsIncluded") || "What's included"}</h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {/* Free plan features */}
                        {freePlan && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-2">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-amber-700 flex items-center gap-1.5">
                                    <HiOutlineGift className="h-3.5 w-3.5" /> {freePlan.planName}
                                </p>
                                {getPlanFeatures(freePlan, lang).length > 0
                                    ? getPlanFeatures(freePlan, lang).map((f, i) => (
                                        <p key={i} className="flex items-start gap-2 text-xs text-slate-600">
                                            <HiOutlineXCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-400" />
                                            {f}
                                        </p>
                                    ))
                                    : (
                                        <p className="text-xs text-slate-400 italic">Browse profiles with limited access</p>
                                    )
                                }
                            </div>
                        )}
                        {/* Best paid plan features */}
                        {paidPlans.length > 0 && (() => {
                            const best = paidPlans[paidPlans.length - 1]
                            const feats = getPlanFeatures(best, lang)
                            return (
                                <div className="rounded-xl border border-primeColor/30 bg-primeSoft/30 p-4 space-y-2">
                                    <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-primeColor flex items-center gap-1.5">
                                        <HiOutlineShieldCheck className="h-3.5 w-3.5" /> {best.planName}
                                    </p>
                                    {feats.length > 0
                                        ? feats.map((f, i) => (
                                            <p key={i} className="flex items-start gap-2 text-xs text-slate-600">
                                                <HiOutlineCheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primeColor" />
                                                {f}
                                            </p>
                                        ))
                                        : (
                                            <>
                                                {["Unlimited profile views","Full contact details","Jathagam & Rasi details","Porutham compatibility","Priority listing"].map((f,i) => (
                                                    <p key={i} className="flex items-start gap-2 text-xs text-slate-600">
                                                        <HiOutlineCheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primeColor" />
                                                        {f}
                                                    </p>
                                                ))}
                                            </>
                                        )
                                    }
                                </div>
                            )
                        })()}
                    </div>
                </div>
            )}

            {/* ── Plan cards ── */}
            <div>
                <h4 className="text-sm font-bold text-tColor mb-3">{t("allPlans") || "All Plans"}</h4>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {sortedPlans.map((plan) => {
                        const tier      = getTier(plan.planName)
                        const colors    = TIER_COLOR[tier]
                        const isCurrent = plan._id === currentPlanId && isActive
                        const isFree    = plan.isFree || plan.planRate === 0
                        const myRank    = planRank(plan._id)
                        const isUpgrade = !isFree && (!isActive || myRank > currentRank)
                        const isDgrade  = !isFree && isActive && myRank < currentRank
                        const isRenew   = isCurrent
                        const features  = getPlanFeatures(plan, lang)

                        return (
                            <motion.div
                                key={plan._id}
                                ref={isCurrent ? currentCardRef : null}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`relative rounded-2xl border-2 p-5 space-y-4 transition
                                    ${isCurrent
                                        ? "border-[#1a8a3a] bg-[#f2fbf4] shadow-md"
                                        : `${colors.border} ${colors.bg}`
                                    }`}
                            >
                                {/* Current badge */}
                                {isCurrent && (
                                    <span className="absolute -top-3 left-4 inline-flex items-center gap-1 rounded-full bg-[#1a8a3a] px-3 py-0.5 text-[11px] font-bold text-white shadow">
                                        <HiOutlineStar className="h-3 w-3" /> {t("currentPlanBadge") || "Current Plan"}
                                    </span>
                                )}

                                {/* Header */}
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <p className="text-base font-bold text-tColor">{plan.planName}</p>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            {isFree
                                                ? `${plan.planValidity || plan.validityInDays || 7} ${t("days") || "days"}`
                                                : `${plan.planValidity || plan.validityInDays || 30} ${t("days") || "days"}`
                                            }
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        {isFree
                                            ? <p className="text-xl font-bold text-slate-600">{t("free") || "Free"}</p>
                                            : <p className="text-xl font-bold text-primeColor">₹{plan.planRate.toLocaleString("en-IN")}</p>
                                        }
                                    </div>
                                </div>

                                {/* Per-day cost */}
                                {!isFree && plan.planRate > 0 && (
                                    <p className="text-[11px] text-slate-400">
                                        ≈ ₹{Math.round(plan.planRate / (plan.planValidity || plan.validityInDays || 30))}/{t("day") || "day"}
                                    </p>
                                )}

                                {/* Features list */}
                                {features.length > 0 && (
                                    <ul className="space-y-1.5">
                                        {features.map((f, i) => (
                                            <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                                                <HiOutlineCheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#1a8a3a]" />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {/* Action button */}
                                {isFree ? (
                                    <p className="text-xs text-slate-500 italic">{t("freePlanNote") || "You start here automatically"}</p>
                                ) : isRenew ? (
                                    <button type="button" onClick={() => onSelectPlan(plan)}
                                        className="w-full rounded-xl bg-[#1a8a3a] py-2.5 text-sm font-bold text-white hover:bg-[#156b2e] transition">
                                        🔄 {t("renewPlan") || "Renew Plan"}
                                    </button>
                                ) : isUpgrade ? (
                                    <button type="button" onClick={() => onSelectPlan(plan)}
                                        className={`w-full rounded-xl py-2.5 text-sm font-bold text-white transition flex items-center justify-center gap-2 ${colors.btn}`}>
                                        <HiOutlineArrowUpCircle className="h-4 w-4" />
                                        {t("upgradeTo") || "Upgrade to"} {plan.planName}
                                    </button>
                                ) : isDgrade ? (
                                    <button type="button" onClick={() => onSelectPlan(plan)}
                                        className="w-full rounded-xl border border-slate-300 bg-white py-2.5 text-sm font-bold text-slate-600 hover:border-slate-400 transition flex items-center justify-center gap-2">
                                        <HiOutlineArrowDownCircle className="h-4 w-4" />
                                        {t("switchTo") || "Switch to"} {plan.planName}
                                    </button>
                                ) : (
                                    <button type="button" onClick={() => onSelectPlan(plan)}
                                        className={`w-full rounded-xl py-2.5 text-sm font-bold text-white transition ${colors.btn}`}>
                                        {t("subscribe") || "Subscribe"}
                                    </button>
                                )}
                            </motion.div>
                        )
                    })}
                </div>
            </div>

            {/* ── Payment security note ── */}
            <div className="flex items-center justify-center gap-2 text-xs text-slate-400 py-2">
                <HiOutlineLockClosed className="h-3.5 w-3.5" />
                Secured by Razorpay · UPI · GPay · PhonePe · Debit / Credit Cards
            </div>
        </div>
    )
}

export default PlansTab
